import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { formatCurrency, formatDate } from "../../utils/utils";
import { useToast } from "../../components/Toast";
import { verifyPayment } from "../../utils/paymenthandler";
import { useNavigate } from "react-router-dom";
import { PaymentOrderData } from "./PaymentStatus";

// Cashfree SDK types based on official documentation
interface CashfreeInstance {
  checkout: (options: CheckoutOptions) => Promise<any>;
}

interface CheckoutOptions {
  paymentSessionId: string;
  redirectTarget?: "_self" | "_blank" | "_top" | "_modal" | string;
}

// Global Cashfree function from CDN
declare global {
  interface Window {
    Cashfree: (config: { mode: "sandbox" | "production" }) => CashfreeInstance;
    Razorpay: any;
  }
}

interface PaymentDueProps {
  paymentDue: {
    isSuccess: boolean;
    data: PaymentOrderData[];
  };
  fromDashboard?: boolean;
}

const PaymentDue: React.FC<PaymentDueProps> = ({
  paymentDue,
  fromDashboard = false,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  useEffect(() => {
    const loadRazorpaySDK = () => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => {
        setRazorpayLoaded(true);
      };
      document.head.appendChild(script);
    };

    loadRazorpaySDK();
  }, [showToast]);

  if (!paymentDue?.isSuccess || !paymentDue?.data?.length) {
    return null;
  }

  const payment = paymentDue?.data[0];

  const dueDate = new Date(payment?.paymentDueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);
  const daysUntilDue = Math.floor(
    (dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  const isOverdue = daysUntilDue < 0;
  const isDueSoon = daysUntilDue >= 0 && daysUntilDue <= 7;

  const getStatusMessage = () => {
    if (isOverdue) {
      return `Overdue by ${Math.abs(daysUntilDue)} day${
        Math.abs(daysUntilDue) !== 1 ? "s" : ""
      }`;
    }
    if (isDueSoon) {
      return daysUntilDue === 0
        ? "Due today"
        : `Due in ${daysUntilDue} day${daysUntilDue !== 1 ? "s" : ""}`;
    }
    return `Due in ${daysUntilDue} day${daysUntilDue !== 1 ? "s" : ""}`;
  };

  const statusMessage = getStatusMessage();
  const isUrgent = isOverdue || isDueSoon;

  const handleRazorpayPayNow = () => {
    setLoading(true);

    const razorpayOptions = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: payment.totalAmount,
      currency: "INR",
      order_id: payment.orderId,
      name: payment.customerName || "Gaadi Kharcha",
      description: payment.description || "Account activation fee",
      modal: {
        escape: false,
        ondismiss: () => {
          showToast("Payment was cancelled", "warning");
          setLoading(false);
        },
      },
      handler: async (paymentResponse: any) => {
        await handlePaymentSuccess(paymentResponse, payment.companyId);
      },
      prefill: {
        name: payment.customerName || "",
        email: payment.customerEmail || "",
        phone: payment.customerPhone || "",
      },
      theme: {
        color: "#1976d2",
      },
    };

    const razorpay = new window.Razorpay(razorpayOptions);

    razorpay.on("payment.failed", (response: any) => {
      console.error("Razorpay payment failed:", response.error);
      showToast(
        response.error?.description || "Payment failed. Please try again.",
        "error"
      );
    });

    razorpay.open();
  };
  const handlePaymentSuccess = async (
    paymentResponse: any,
    companyId: string
  ) => {
    try {
      if (!paymentResponse || typeof paymentResponse !== "object") {
        throw new Error("Invalid payment response format");
      }

      if (
        !paymentResponse.razorpay_payment_id ||
        !paymentResponse.razorpay_order_id ||
        !paymentResponse.razorpay_signature
      ) {
        throw new Error("Missing required payment details in response");
      }

      if (!paymentResponse.razorpay_payment_id.startsWith("pay_")) {
        throw new Error("Invalid payment ID format");
      }

      if (!paymentResponse.razorpay_order_id.startsWith("order_")) {
        throw new Error("Invalid order ID format");
      }

      const response = await verifyPayment(paymentResponse, companyId, 30);
      if (response?.data?.isSuccess) {
        showToast("Payment completed successfully", "success");
        if (fromDashboard) {
          window.location.reload();
        } else {
          navigate("/dashboard");
        }
      } else {
        throw new Error(
          response?.data?.message || "Payment verification failed"
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.message ||
        "Payment completed but verification failed. Please contact support with your payment details.";
      showToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const PaymentModal = () => (
    <Dialog
      open={openModal}
      onClose={() => setOpenModal(false)}
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight="600">
          Complete Payment
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Alert
            severity={isUrgent ? "warning" : "info"}
            sx={{
              mb: 3,
              borderRadius: 1,
              "& .MuiAlert-message": {
                fontSize: "0.875rem",
              },
            }}
          >
            {statusMessage} - Please complete your payment to continue using
            Gaadi Kharcha services.
          </Alert>

          <Box
            sx={{
              p: 3,
              mb: 3,
              bgcolor: "#f8f9fa",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Order ID
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {payment.orderId}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Amount
                </Typography>
                <Typography variant="h6" fontWeight="600" color="primary.main">
                  {formatCurrency(payment.totalAmount)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Due Date
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {formatDate(payment?.paymentDueDate)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Type
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {payment.orderType}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={() => setOpenModal(false)}
          variant="outlined"
          disabled={loading}
          sx={{
            textTransform: "none",
            fontWeight: "500",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleRazorpayPayNow}
          variant="contained"
          disabled={loading || !razorpayLoaded}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{
            minWidth: 120,
            textTransform: "none",
            fontWeight: "600",
          }}
        >
          {loading ? "Redirecting..." : "Pay Now"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <Container sx={{ mb: 1, width: "100%", px: 0 }}>
        <Paper
          elevation={2}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "#ffffff",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              pb: 1,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6" fontWeight="600" color="text.primary">
              Payment Due
            </Typography>
            <Box
              sx={{
                px: 1.5,
                py: 0.3,
                borderRadius: 1,
                bgcolor: isUrgent ? "#fff3e0" : "#e3f2fd",
                color: isUrgent ? "#e65100" : "#1565c0",
              }}
            >
              <Typography variant="caption" fontWeight="500">
                {statusMessage}
              </Typography>
            </Box>
          </Box>

          {/* Main Content */}
          <Grid container spacing={2}>
            {/* Payment Details & Amount Breakdown */}
            <Grid item xs={12} md={10}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    Due Date
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatDate(payment?.paymentDueDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    Order Type
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {payment?.orderType}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      bgcolor: "#f8f9fa",
                      p: 2,
                      borderRadius: 1.5,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    {/* Amount & Unit Price Header */}
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      color="text.primary"
                      sx={{ mb: 1.5 }}
                    >
                      Order Breakdown for {payment?.noOfUnits} Vehicle(s)
                    </Typography>

                    <Box sx={{ mb: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          Original Amount ({payment?.noOfUnits} ×{" "}
                          {formatCurrency(Number(payment?.unitPrice))})
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          color="text.primary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {formatCurrency(
                            Number(payment?.unitPrice) * payment?.noOfUnits
                          )}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Discounted Amount Calculation */}
                    {Number(payment?.discountedUnitPrice) > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          Discounted Amount ({payment?.noOfUnits} ×{" "}
                          {formatCurrency(Number(payment.discountedUnitPrice))})
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          color="text.primary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {formatCurrency(
                            Number(payment.discountedUnitPrice) *
                              payment?.noOfUnits
                          )}
                        </Typography>
                      </Box>
                    ) : null}

                    {/* Onboarding Fee */}
                    {payment?.onboardingAmount &&
                      Number(payment.onboardingAmount) > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{ fontSize: "0.8rem" }}
                          >
                            Onboarding Fee (one-time)
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            color="text.primary"
                            sx={{ fontSize: "0.8rem" }}
                          >
                            {formatCurrency(Number(payment?.onboardingAmount))}
                          </Typography>
                        </Box>
                      )}

                    {/* Total Amount */}
                    <Box
                      sx={{
                        pt: 1,
                        borderTop: "2px solid #e0e0e0",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          color="text.primary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          Total Amount
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color="primary.main"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {formatCurrency(payment.totalAmount)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Pay Now Button */}
            <Grid item xs={12} md={2}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Button
                  variant="contained"
                  size="medium"
                  onClick={() => setOpenModal(true)}
                  sx={{
                    py: 0.5,
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: "0 2px 8px rgba(25, 118, 210, 0.25)",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(25, 118, 210, 0.35)",
                    },
                  }}
                >
                  Pay Now
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <PaymentModal />
    </>
  );
};

export default PaymentDue;
